import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { OpUnitType } from 'dayjs'
import { ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { z } from 'zod'

import { dayjs } from '@/lib/dayjs'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs.tsx'
import { QuerySpinner } from '@/components/spinner/query-spinner.tsx'
import { apiClient, formatClientResponse } from '@/apiClient'
import { useGetEvent } from '@/features/calendar/hooks/use-get-event.ts'
import { CalendarEvent } from '@/features/calendar/types'
import { ScheduleView } from '@/routes/_authenticated/calendar/-components/schedule-view.tsx'

import { MonthView } from './-components/month-view.tsx'
import { WeekView } from './-components/week-view.tsx'
import { YearView } from './-components/year-view.tsx'

const searchParams = z
  .object({
    currentDate: z.coerce.date().optional(),
    eventId: z.string().optional(),
    view: z.enum(['week', 'month', 'year', 'schedule']).optional(),
  })
  .optional()

type SearchParams = NonNullable<z.infer<typeof searchParams>>

export const Route = createFileRoute('/_authenticated/calendar/')({
  component: Calendar,
  validateSearch: searchParams,
})

type CalendarView = 'week' | 'month' | 'year' | 'schedule'

export default function Calendar() {
  const searchParams = {
    currentDate: new Date(),
    view: 'month' as SearchParams['view'],
    ...Route.useSearch(),
  }
  const { currentDate, view } = searchParams
  const navigate = Route.useNavigate()

  const { startDate, endDate } = useMemo(() => {
    const unit = view === 'schedule' ? 'day' : (view as OpUnitType)
    const startDate = dayjs(currentDate).startOf(unit).toISOString()
    const endDate =
      view === 'schedule'
        ? dayjs(currentDate).add(6, 'years').endOf(unit).toISOString()
        : dayjs(currentDate).endOf(unit).toISOString()

    return { startDate, endDate }
  }, [currentDate, view])

  const viewLabel = useMemo(
    () =>
      view
        ? {
            week: 'Settimana',
            month: 'Mese',
            year: 'Anno',
            schedule: 'Prossimi Eventi',
          }[view]
        : null,
    [view]
  )

  const findEventsQuery = useQuery({
    queryKey: ['calendar-events', startDate, endDate],
    queryFn: () => {
      return apiClient.calendar.events
        .$get({
          query: {
            startDate,
            endDate,
          },
        })
        .then((r) => formatClientResponse(r))
    },
  })
  const events: CalendarEvent[] | undefined = findEventsQuery?.data?.data

  const getEventQuery = useGetEvent(
    { eventId: searchParams.eventId! },
    { enabled: !!searchParams.eventId }
  )
  const selectedEvent: CalendarEvent | null = getEventQuery?.data?.success
    ? getEventQuery?.data?.data
    : null

  useEffect(() => {
    if (selectedEvent) {
      setCurrentDate(dayjs(selectedEvent.start_date).toDate(), true)
    }
  }, [selectedEvent])

  const setView = (view: CalendarView) => {
    navigate({
      search: { currentDate, view },
    })
  }

  const setCurrentDate = (
    newDate: Date | ((currentDate?: Date) => Date | undefined),
    replaceNavigation = false
  ) => {
    const date = typeof newDate === 'function' ? newDate(currentDate) : newDate

    navigate({
      search: {
        ...searchParams,
        view: view ?? 'month',
        currentDate: date ?? new Date(),
      },
      replace: replaceNavigation,
    })
  }

  const paginateDate = (amount: number) => {
    setCurrentDate((date) => {
      const prevDate = dayjs(date)
      switch (view) {
        case 'month':
          return prevDate.add(amount, 'month').startOf('month').toDate()
        case 'week':
          return prevDate.add(amount, 'week').toDate()
        case 'year':
          return prevDate.add(amount, 'year').startOf('year').toDate()
        default:
          return prevDate.toDate()
      }
    })
  }

  const goToToday = () => setCurrentDate(new Date())

  return (
    <div className="flex h-full flex-col p-4">
      <div className="mb-4 flex shrink items-center justify-between">
        <div className="flex items-center">
          <DropdownMenu>
            <DropdownMenuTrigger className="lg:hidden" asChild>
              <Button variant="outline">
                {viewLabel} <ChevronsUpDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="lg:hidden">
              <DropdownMenuItem onClick={() => setView('month')}>
                Mese
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setView('week')}>
                Settimana
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setView('year')}>
                Anno
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setView('schedule')}>
                Prossimi eventi
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Tabs
            className="hidden flex-col overflow-hidden pr-4 lg:flex"
            value={view}
            onValueChange={(view) =>
              setView(view as NonNullable<SearchParams['view']>)
            }
          >
            <TabsList>
              <TabsTrigger value="month">Mese</TabsTrigger>
              <TabsTrigger value="week">Settimana</TabsTrigger>
              <TabsTrigger value="year">Anno</TabsTrigger>
              <TabsTrigger value="schedule">Agenda</TabsTrigger>
            </TabsList>
          </Tabs>

          <QuerySpinner
            spinnerClassName="ml-4 size-4"
            query={findEventsQuery}
            retryingMessageVisible={false}
            ErrorComponent={({ onRetry }) => (
              <div>
                Errore
                <Button
                  className="ml-2"
                  variant="outline"
                  size="sm"
                  onClick={onRetry}
                >
                  Riprova
                </Button>
              </div>
            )}
          />
          {/*{findEventsQuery.isPending && <SpinnerIcon className="ml-4 size-4" />}*/}
        </div>

        <div className="flex items-center gap-4">
          {view !== 'schedule' && (
            <>
              <h2 className="text-2xl font-semibold first-letter:uppercase">
                {dayjs(currentDate).format(
                  view === 'year' ? 'YYYY' : 'MMMM YYYY'
                )}
              </h2>

              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => paginateDate(-1)}
                >
                  <ChevronLeft className="size-4" />
                </Button>
                <Button variant="outline" onClick={goToToday}>
                  Oggi
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => paginateDate(1)}
                >
                  <ChevronRight className="size-4" />
                </Button>
              </div>
            </>
          )}
          {/*<Button>Aggiungi evento</Button>*/}
        </div>
      </div>

      <ScrollArea className="grow">
        {view === 'month' && (
          <MonthView currentDate={currentDate} events={events} />
        )}
        {view === 'week' && (
          <WeekView currentDate={currentDate} events={events} />
        )}
        {view === 'year' && (
          <YearView currentDate={currentDate} events={events} />
        )}
        {view === 'schedule' && (
          <ScheduleView
            currentDate={currentDate}
            events={events}
            isLoading={findEventsQuery.isPending}
          />
        )}
      </ScrollArea>
    </div>
  )
}
